import { mapById, without } from '@dmp/shared/helpers';
import { makeSurveyDirty } from '../utils/make-survey-dirty';
import {
    isDynamicCoreQuestionItem,
    isForkedAnswerItem,
    isLinearQuestionItem,
} from '../../state-type-guards';
import type { AnswerState, CardState, StoreState } from '../../state-types';

/** Delete card, includeing its related answers and connections */
export const deleteCard = (
    state: StoreState,
    removeCardId: string
): StoreState => {
    const cardIds = state.survey.cardIds;

    // cannot remove the last card
    if (cardIds.length <= 1) {
        return state;
    }

    // remove the card and
    // remove all connections in `resultCard` to the removed card
    const { newCardList, newAnswerList } = cleanResultCardConnections(
        Object.values(state.cards),
        Object.values(state.answers),
        removeCardId
    );

    return makeSurveyDirty({
        ...state,
        survey: {
            ...state.survey,
            // remove the card id from the survey
            cardIds: without(cardIds, [removeCardId]),
        },
        cards: mapById(newCardList),
        answers: mapById(newAnswerList),
    });
};

// remove all connections in `resultCard` to the removed card
const cleanResultCardConnections = (
    cardList: CardState[],
    answerList: AnswerState[],
    removeCardId: string
) => {
    const newCardList = cardList
        // remove the card first
        .filter((c) => c.id !== removeCardId)
        // then remove all connections in `resultCard` to the removed card
        .map((card) => {
            if (isLinearQuestionItem(card) || isDynamicCoreQuestionItem(card)) {
                if (card.resultCard === removeCardId) {
                    return { ...card, resultCard: undefined };
                }
            }

            return card;
        });

    // remove answers that have `resultCard` points to the removed card
    const newAnswerList = answerList.map((answer) => {
        if (isForkedAnswerItem(answer)) {
            if (answer.resultCard === removeCardId) {
                return { ...answer, resultCard: undefined };
            }
        }

        return answer;
    });

    return {
        newCardList,
        newAnswerList,
    };
};
